// The Avenue School/the-avenue-school/Client/src/components/ContactUs.js
import React, { useState } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaFacebookF, FaLinkedin, FaInstagram } from 'react-icons/fa';
import '../styles/contactus.css';
import axios from 'axios';
import PageHeader from './PageHeader';
import { API_BASE } from '../lib/apiBase';

/**
 * ContactUs page
 * - Left: school info (eyebrow, title, intro + address/email/phone)
 * - Right: card with your existing form (kept intact; styled via CSS classes)
 * - Map embed below, centered, max-width 1000px
 */

const ContactUs = ({
  mapSrc = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6297.9070924079715!2d145.29278976190182!3d-37.884769054322625!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad63d6c7ac7ded5%3A0xd0837b2a3d565378!2sThe%20Avenue%20School!5e0!3m2!1sen!2sau!4v1760611564588!5m2!1sen!2sau",
  children,
}) => {
  // ---------- form state & handlers (added) ----------
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Mirror backend’s basic requirements for fast feedback
  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setSubmitted(false);
      return;
    }

    try {
      setSubmitting(true);
      setErrors({});
      // Backend mount is /contact (no /api prefix)
      await axios.post(`${API_BASE}/contacts`, formData, { withCredentials: true });
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      console.error('📛 Error submitting contact form:', err);
      alert('Something went wrong. Please try again later.');
      setSubmitted(false);
    } finally {
      setSubmitting(false);
    }
  };
  // ---------------------------------------------------

  return (
    <main className="contactPage">
      <PageHeader
        title="Contact Us"
        image="/media/gallery-6.jpg"
        height="200px"
      />
      <Container className="contactPage__wrap">
        <Row className="g-4 align-items-start">
          {/* Left column: supporting content */}
          <Col lg={6}>
            <section className="contactInfo" aria-labelledby="contact-info-heading">
              <header className="contactInfo__header">
                <p className="contactInfo__eyebrow">THE AVENUE SCHOOL</p>
                <h1 id="contact-info-heading" className="contactInfo__title">Contact Us</h1>
                <p className="contactInfo__lead">
                  We’re here to help. Reach out with questions about enrolment, curriculum, or support services—our team is ready to guide you through your journey at The Avenue School.
                </p>
              </header>

              <div className="contactInfo__blocks">
                <div className="contactInfo__block">
                  <div className="contactInfo__label">Address</div>
                  <div className="contactInfo__value">
                    13–15 The Avenue<br />
                    Ferntree Gully VIC 3156
                  </div>
                </div>

                <div className="contactInfo__block">
                  <div className="contactInfo__label">Email</div>
                  <div className="contactInfo__value">office@theavenueschool.vic.edu.au</div>
                </div>

                <div className="contactInfo__block">
                  <div className="contactInfo__label">Phone</div>
                  <div className="contactInfo__value">(03) 9758 7859</div>
                </div>
              </div>

              
              {/* Social media icons */}
 <div className="contactInfo__social">
   <a
     href="https://www.facebook.com/profile.php?id=61576600509884"
     target="_blank"
     rel="noopener noreferrer"
     aria-label="The Avenue School on Facebook"
    className="contactInfo__socialLink"
   >
     <i className="contactInfo__socialIcon"><FaFacebookF /></i>
   </a>
   <a
     href="https://instagram.com/theavenueschool"
     target="_blank"
     rel="noopener noreferrer"
     aria-label="The Avenue School on Instagram"
     className="contactInfo__socialLink"
   >
     <i className="contactInfo__socialIcon"><FaInstagram /></i>
   </a>
   <a
     href="https://www.linkedin.com/company/105613790/"
     target="_blank"
     rel="noopener noreferrer"
     aria-label="The Avenue School on LinkedIn"
     className="contactInfo__socialLink"
   >
     <i className="contactInfo__socialIcon"><FaLinkedin /></i>
   </a>
 </div>
            </section>
          </Col>

          {/* Right column: your existing form in a card */}
          <Col lg={6}>
            <Card className="contactFormCard shadow-sm">
              <Card.Body className="contactFormCard__body">
                {/* If you prefer, pass a form via children */}
                {children}

                {/* Embedded form (your original, now wired & styled) */}
                <form onSubmit={handleSubmit} noValidate>
                  <div className="mb-3">
                    <label className="form-label" style={{ color: '#2b333d' }}>Name *</label>
                    <input
                      type="text"
                      className={`form-control contactForm__control ${errors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Name"
                      required
                    />
                    <div className="invalid-feedback">{errors.name}</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label" style={{ color: '#2b333d' }}>Email *</label>
                    <input
                      type="email"
                      className={`form-control contactForm__control ${errors.email ? 'is-invalid' : ''}`}
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email"
                      required
                    />
                    <div className="invalid-feedback">{errors.email}</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label" style={{ color: '#2b333d' }}>Phone</label>
                    <input
                      type="text"
                      className="form-control contactForm__control"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Phone"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label" style={{ color: '#2b333d' }}>Subject</label>
                    <select
                      className="form-select contactForm__control"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="support">Support</option>
                      <option value="admission">Admission</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label" style={{ color: '#2b333d' }}>Message *</label>
                    <textarea
                      className={`form-control contactForm__textarea ${errors.message ? 'is-invalid' : ''}`}
                      name="message"
                      rows="6"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Please type your message here..."
                      required
                    />
                    <div className="invalid-feedback">{errors.message}</div>
                  </div>

                  <button
                    type="submit"
                    className="btn contactForm__submit w-100 fw-semibold text-uppercase"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting…' : 'Send message'}
                  </button>

                  {submitted && (
                    <div className="alert alert-success mt-3 text-center">
                      Thank you! Your message has been sent.
                    </div>
                  )}
                </form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Map embed (centered, max-width: 1000px) */}
      <section className="contactMap" aria-label="Map showing the location of The Avenue School">
        <div className="contactMap__inner">
          <div className="contactMap__frame">
            <iframe
              title="The Avenue School on Google Maps"
              src={mapSrc}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactUs;


